'use client'

import Link from 'next/link'
import Image from 'next/image'
import {
    Bot, Brain, Cpu, Code, Download, FileText, MessageSquare,
    Zap, Shield, Globe, Terminal, ArrowRight, CheckCircle,
    Home as HomeIcon, Mic, Network, Clock, Book, ExternalLink,
    Play, ChevronRight, Sparkles
} from 'lucide-react'

const sdkFeatures = [
    {
        icon: Brain,
        title: "Perfect Recall",
        description: "RAG-powered memory gives your robot instant access to the user's personal knowledge base. Every interaction is contextually aware."
    },
    {
        icon: MessageSquare,
        title: "Psych-Sync Protocol",
        description: "Emotional intelligence engine that adapts communication style, detects user sentiment, and responds with appropriate empathy."
    },
    {
        icon: HomeIcon,
        title: "IoT Commander",
        description: "Control 1,000+ smart home devices through natural language. Works with HomeKit, Matter, Google Home, and Alexa ecosystems."
    },
    {
        icon: Clock,
        title: "Sub-200ms Voice",
        description: "Real-time bidirectional audio powered by Link Ultra Voice. Ultra-low latency for natural conversation flow."
    },
    {
        icon: Shield,
        title: "Privacy-First",
        description: "End-to-end encryption, GDPR compliant, optional on-device processing. User data stays with the user."
    },
    {
        icon: Network,
        title: "Knowledge Graph",
        description: "Visualize and traverse entity relationships. Let your robot understand context and connections between concepts."
    },
]

const apiEndpoints = [
    {
        method: "POST",
        endpoint: "/v1/robot/query",
        description: "Send a natural language query and get RAG-grounded response",
        protocol: "REST"
    },
    {
        method: "WS",
        endpoint: "/v1/robot/voice",
        description: "Real-time bidirectional voice streaming with tool calling",
        protocol: "WebSocket"
    },
    {
        method: "POST",
        endpoint: "/v1/robot/memory",
        description: "Store and retrieve entries from the user's knowledge base",
        protocol: "REST"
    },
    {
        method: "POST",
        endpoint: "/v1/robot/iot/control",
        description: "Control smart home devices by natural language command",
        protocol: "REST"
    },
    {
        method: "gRPC",
        endpoint: "robotics.LinkBox/Query",
        description: "High-performance streaming queries for embedded systems",
        protocol: "gRPC"
    },
]

const documentationFiles = [
    {
        title: "LinkBox Robotics SDK - Quick Start Guide",
        description: "Get your robot connected to LinkBox in under 5 minutes. Covers Python, Node.js, and REST API.",
        href: "/docs/LinkBox_Robotics_QuickStart.md",
        size: "8 KB",
        icon: Play
    },
    {
        title: "API Reference Documentation",
        description: "Complete API reference with all endpoints, parameters, response formats, and error codes.",
        href: "/docs/LinkBox_API_Reference.md",
        size: "12 KB",
        icon: Book
    },
    {
        title: "Integration Architecture Guide",
        description: "System design patterns for integrating cognitive intelligence into robotic platforms.",
        href: "/docs/LinkBox_Integration_Guide.md",
        size: "15 KB",
        icon: Network
    },
    {
        title: "MCP Server Specification",
        description: "Model Context Protocol (MCP) server implementation details for advanced AI tool integration.",
        href: "/docs/LinkBox_MCP_Specification.md",
        size: "18 KB",
        icon: Cpu
    },
]

const pricingTiers = [
    {
        name: "Prototype",
        price: "Free",
        period: "",
        description: "For testing and development",
        features: [
            "1,000 API calls/month",
            "10 connected robots",
            "Basic RAG search",
            "Community support",
            "REST API access"
        ],
        cta: "Start Building",
        highlighted: false
    },
    {
        name: "Startup",
        price: "$499",
        period: "/month",
        description: "For early-stage robotics companies",
        features: [
            "100,000 API calls/month",
            "1,000 connected robots",
            "Full RAG + Voice APIs",
            "Psych-Sync emotional AI",
            "gRPC + WebSocket access",
            "Priority email support",
            "Custom voice personas"
        ],
        cta: "Start Free Trial",
        highlighted: true
    },
    {
        name: "Enterprise",
        price: "Custom",
        period: "",
        description: "For scaled deployments",
        features: [
            "Unlimited API calls",
            "Unlimited robots",
            "On-premise deployment",
            "Dedicated infrastructure",
            "Custom model training",
            "24/7 phone support",
            "SLA guarantees"
        ],
        cta: "Contact Sales",
        highlighted: false
    },
]

export default function RoboticsPage() {
    return (
        <div className="min-h-screen bg-gray-950">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-gray-950/80 backdrop-blur-xl border-b border-gray-800">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Link href="/" className="flex items-center gap-3">
                            <Image
                                src="/linkbox-logo.png"
                                alt="LinkBox AI"
                                width={40}
                                height={40}
                                className="rounded-xl"
                            />
                            <span className="text-xl font-bold">
                                <span className="text-[#4ade80]">Link</span>
                                <span className="text-white">Box</span>
                                <span className="text-gray-400 font-normal"> Robotics</span>
                            </span>
                        </Link>

                        <div className="hidden md:flex items-center gap-8">
                            <Link href="/features" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Features</Link>
                            <a href="#sdk" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">SDK</a>
                            <a href="#pricing" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Pricing</a>
                            <a href="#docs" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Documentation</a>
                        </div>

                        <div className="flex items-center gap-3">
                            <Link href="/login" className="text-gray-300 hover:text-white transition-colors text-sm font-medium px-4 py-2">
                                Log In
                            </Link>
                            <Link
                                href="/signup"
                                className="bg-gradient-to-r from-[#4ade80] to-[#22c55e] text-gray-900 px-5 py-2 rounded-lg text-sm font-semibold hover:from-[#86efac] hover:to-[#4ade80] transition-all shadow-lg shadow-green-500/25"
                            >
                                Get API Key
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero */}
            <section className="relative min-h-screen flex items-center overflow-hidden pt-16">
                <div className="absolute inset-0 bg-gradient-to-br from-[#1e3a5f] via-gray-950 to-[#134e4a]" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(74,222,128,0.15),transparent_50%)]" />

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <div className="inline-flex items-center gap-2 bg-[#4ade80]/10 border border-[#4ade80]/30 rounded-full px-4 py-2 mb-6">
                                <Sparkles className="w-4 h-4 text-[#4ade80]" />
                                <span className="text-[#4ade80] text-sm font-medium">RoboDev 2026 Official Partner</span>
                            </div>

                            <h1 className="text-4xl md:text-6xl font-bold mb-6">
                                <span className="text-white">Give Your Robot a </span>
                                <span className="bg-gradient-to-r from-[#4ade80] to-[#22c55e] bg-clip-text text-transparent">Mind</span>
                            </h1>

                            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                                LinkBox AI is the <strong>Cognition-as-a-Service</strong> layer for 2026 robotics.
                                RAG memory, emotional intelligence, and IoT control in <strong>10 lines of code</strong>.
                            </p>

                            <div className="flex flex-col sm:flex-row gap-4">
                                <Link
                                    href="/signup"
                                    className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#4ade80] to-[#22c55e] text-gray-900 px-8 py-4 rounded-xl font-semibold hover:from-[#86efac] hover:to-[#4ade80] transition-all shadow-lg shadow-green-500/25 text-lg"
                                >
                                    Start Building Free
                                    <ArrowRight className="w-5 h-5" />
                                </Link>
                                <a
                                    href="#docs"
                                    className="inline-flex items-center justify-center gap-2 border border-gray-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-gray-800 transition-colors text-lg"
                                >
                                    View Documentation
                                </a>
                            </div>
                        </div>

                        {/* Code Preview */}
                        <div className="bg-gray-900 rounded-2xl border border-gray-700 overflow-hidden shadow-2xl">
                            <div className="flex items-center gap-2 bg-gray-800 px-4 py-3 border-b border-gray-700">
                                <div className="w-3 h-3 rounded-full bg-red-500" />
                                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                                <div className="w-3 h-3 rounded-full bg-green-500" />
                                <span className="text-gray-400 text-sm ml-2 font-mono">robot_brain.py</span>
                            </div>
                            <div className="p-6 font-mono text-sm overflow-x-auto">
                                <pre className="text-gray-300">
                                    <code>{`from linkbox_ai import LinkBoxRobotClient

# 10 lines to cognitive intelligence
client = LinkBoxRobotClient(
    api_key="lb_your_api_key",
    robot_id="UNIT_01"
)

# Ask anything - grounded in user's knowledge
response = client.query(
    "What did the user save about cooking pasta?"
)

print(response.answer)
# "Based on 3 saved recipes, the user prefers
#  al dente pasta with garlic butter sauce..."

# Voice conversation with emotional intelligence
async for audio in client.voice_stream(
    personality="helpful_companion",
    enable_psych_sync=True
):
    robot.speakers.play(audio)`}</code>
                                </pre>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* SDK Features */}
            <section id="sdk" className="relative py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                            Cognitive Capabilities
                        </h2>
                        <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                            Everything your robot needs to understand, remember, and connect.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {sdkFeatures.map((feature, index) => (
                            <div
                                key={index}
                                className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8 hover:border-[#4ade80]/50 transition-all duration-300"
                            >
                                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#4ade80]/20 to-[#22c55e]/10 flex items-center justify-center mb-6">
                                    <feature.icon className="w-7 h-7 text-[#4ade80]" />
                                </div>
                                <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
                                <p className="text-gray-400 leading-relaxed">{feature.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* API Endpoints */}
            <section className="relative py-20 bg-gray-900/30">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                            API Reference
                        </h2>
                        <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                            REST, WebSocket, and gRPC endpoints for every integration pattern.
                        </p>
                    </div>

                    <div className="grid gap-4">
                        {apiEndpoints.map((api, index) => (
                            <div
                                key={index}
                                className="bg-gray-900 border border-gray-800 rounded-xl p-6 flex flex-col md:flex-row md:items-center gap-4"
                            >
                                <div className="flex items-center gap-4 flex-1">
                                    <span className={`px-3 py-1 rounded-lg text-xs font-bold ${api.method === 'POST' ? 'bg-blue-500/20 text-blue-400' :
                                        api.method === 'WS' ? 'bg-purple-500/20 text-purple-400' :
                                            'bg-green-500/20 text-green-400'
                                        }`}>
                                        {api.method}
                                    </span>
                                    <code className="text-[#4ade80] font-mono">{api.endpoint}</code>
                                </div>
                                <p className="text-gray-400 flex-1">{api.description}</p>
                                <span className="text-xs text-gray-500 bg-gray-800 px-3 py-1 rounded-full">{api.protocol}</span>
                            </div>
                        ))}
                    </div>

                    <div className="mt-8 text-center">
                        <code className="bg-gray-900 border border-gray-700 px-6 py-3 rounded-xl text-[#4ade80] font-mono inline-flex items-center gap-2">
                            <Terminal className="w-4 h-4" />
                            $ pip install linkbox-robotics
                        </code>
                    </div>
                </div>
            </section>

            {/* Documentation Downloads */}
            <section id="docs" className="relative py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                            Documentation & Resources
                        </h2>
                        <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                            Download our comprehensive guides to get started with LinkBox Robotics.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                        {documentationFiles.map((doc, index) => (
                            <div
                                key={index}
                                className="bg-gray-900 border border-gray-800 rounded-2xl p-6 hover:border-[#4ade80]/50 transition-all duration-300 group"
                            >
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-[#4ade80]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#4ade80]/20 transition-colors">
                                        <doc.icon className="w-6 h-6 text-[#4ade80]" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-lg font-semibold text-white mb-2">{doc.title}</h3>
                                        <p className="text-gray-400 text-sm mb-4">{doc.description}</p>
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs text-gray-500">{doc.size} • Markdown</span>
                                            <a
                                                href={doc.href}
                                                download
                                                className="inline-flex items-center gap-2 text-[#4ade80] hover:text-[#86efac] text-sm font-medium transition-colors"
                                            >
                                                <Download className="w-4 h-4" />
                                                Download
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-12 text-center">
                        <p className="text-gray-400 mb-4">Need more resources?</p>
                        <div className="flex flex-wrap justify-center gap-4">
                            <a href="#" className="inline-flex items-center gap-2 text-gray-300 hover:text-white transition-colors">
                                <Book className="w-4 h-4" />
                                API Docs
                                <ExternalLink className="w-3 h-3" />
                            </a>
                            <a href="#" className="inline-flex items-center gap-2 text-gray-300 hover:text-white transition-colors">
                                <Code className="w-4 h-4" />
                                GitHub Examples
                                <ExternalLink className="w-3 h-3" />
                            </a>
                            <a href="#" className="inline-flex items-center gap-2 text-gray-300 hover:text-white transition-colors">
                                <MessageSquare className="w-4 h-4" />
                                Developer Discord
                                <ExternalLink className="w-3 h-3" />
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Pricing */}
            <section id="pricing" className="relative py-20 bg-gray-900/30">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                            Simple, Scalable Pricing
                        </h2>
                        <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                            Start free, scale with your deployment. No hidden fees.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {pricingTiers.map((tier, index) => (
                            <div
                                key={index}
                                className={`rounded-2xl p-8 ${tier.highlighted
                                    ? 'bg-gradient-to-br from-[#4ade80]/20 to-[#22c55e]/10 border-2 border-[#4ade80] relative'
                                    : 'bg-gray-900 border border-gray-800'
                                    }`}
                            >
                                {tier.highlighted && (
                                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#4ade80] text-gray-900 px-4 py-1 rounded-full text-sm font-semibold">
                                        Most Popular
                                    </div>
                                )}

                                <h3 className="text-xl font-semibold text-white mb-2">{tier.name}</h3>
                                <p className="text-gray-400 text-sm mb-6">{tier.description}</p>

                                <div className="mb-6">
                                    <span className="text-4xl font-bold text-white">{tier.price}</span>
                                    <span className="text-gray-400">{tier.period}</span>
                                </div>

                                <ul className="space-y-3 mb-8">
                                    {tier.features.map((feature, featureIndex) => (
                                        <li key={featureIndex} className="flex items-center gap-3 text-gray-300">
                                            <CheckCircle className="w-5 h-5 text-[#4ade80] flex-shrink-0" />
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>

                                <Link
                                    href="/signup"
                                    className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold transition-all ${tier.highlighted
                                        ? 'bg-[#4ade80] text-gray-900 hover:bg-[#86efac]'
                                        : 'border border-gray-600 text-white hover:bg-gray-800'
                                        }`}
                                >
                                    {tier.cta}
                                    <ChevronRight className="w-4 h-4" />
                                </Link>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="relative py-20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-4xl font-bold text-white mb-6">
                        Ready to Give Your Robot a Mind?
                    </h2>
                    <p className="text-xl text-gray-400 mb-8">
                        Join 50+ robotics companies building with LinkBox Cognition-as-a-Service.
                    </p>
                    <Link
                        href="/signup"
                        className="inline-flex items-center gap-2 bg-gradient-to-r from-[#4ade80] to-[#22c55e] text-gray-900 px-10 py-4 rounded-xl font-semibold hover:from-[#86efac] hover:to-[#4ade80] transition-all shadow-lg shadow-green-500/25 text-lg"
                    >
                        Get Your API Key
                        <ArrowRight className="w-5 h-5" />
                    </Link>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-gray-800 py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="flex items-center gap-3">
                            <Image
                                src="/linkbox-logo.png"
                                alt="LinkBox AI"
                                width={32}
                                height={32}
                                className="rounded-lg"
                            />
                            <span className="text-gray-400 text-sm">© 2026 BB23 LLC. All rights reserved.</span>
                        </div>
                        <div className="flex items-center gap-6">
                            <Link href="/privacy" className="text-gray-400 hover:text-white text-sm transition-colors">Privacy</Link>
                            <Link href="/terms" className="text-gray-400 hover:text-white text-sm transition-colors">Terms</Link>
                            <Link href="https://bb23llc.com" className="text-gray-400 hover:text-white text-sm transition-colors">BB23 LLC</Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    )
}
